import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import VisualMappingEditor from './VisualMappingEditor';
import './AdminAgreementsSection.css';

function normId(item) {
  if (!item) return undefined;
  const id = item.id ?? item._id;
  return typeof id === 'object' && id?.$oid != null ? id.$oid : id;
}

const PDFIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

// Ensure these fields always appear in the form so employee name/designation can be filled
const EXPECTED_FIELD_KEYS = [
  'company_name', 'company_address', 'company_contact', 'company_phone', 'company_email',
  'employee_name', 'employee_code', 'employee_email', 'employee_phone', 'employee_designation', 'employee_joining_date',
  'current_date', 'agreement_date', 'day', 'start_date', 'end_date', 'agreement_validity_from', 'agreement_validity_to',
  'salary', 'terms', 'working_hours', 'working_days', 'replacement_working_days', 'payment_before_date',
  'service_provider', 'service_provider_address', 'service_provider_represented_by', 'current_address', 'subject',
  're_agreement_date', 'for_agency', 'for_client'
];

// Section-wise field layout: section title -> list of { key, label, inputType }
const FIELD_SECTIONS = [
  {
    title: 'COMPANY',
    fields: [
      { key: 'company_name', label: 'Company name', inputType: 'text' },
      { key: 'company_address', label: 'Company address', inputType: 'textarea' },
      { key: 'company_contact', label: 'Company contact', inputType: 'text' },
      { key: 'company_phone', label: 'Company phone', inputType: 'tel' },
      { key: 'company_email', label: 'Company email', inputType: 'email' }
    ]
  },
  {
    title: 'EMPLOYEE',
    fields: [
      { key: 'employee_name', label: 'Employee name', inputType: 'text' },
      { key: 'employee_code', label: 'Employee code', inputType: 'text' },
      { key: 'employee_email', label: 'Employee email', inputType: 'email' },
      { key: 'employee_phone', label: 'Employee phone', inputType: 'tel' },
      { key: 'employee_designation', label: 'Employee designation', inputType: 'text' },
      { key: 'employee_joining_date', label: 'Employee joining date', inputType: 'date' },
      { key: 'current_address', label: 'Current address', inputType: 'textarea' }
    ]
  },
  {
    title: 'AGREEMENT DETAILS',
    fields: [
      { key: 'subject', label: 'Subject', inputType: 'text' },
      { key: 'current_date', label: 'Current date', inputType: 'date' },
      { key: 'agreement_date', label: 'Agreement date', inputType: 'date' },
      { key: 'day', label: 'Day', inputType: 'text' },
      { key: 'start_date', label: 'Start date', inputType: 'date' },
      { key: 'end_date', label: 'End date', inputType: 'date' },
      { key: 'agreement_validity_from', label: 'Agreement validity from', inputType: 'date' },
      { key: 'agreement_validity_to', label: 'Agreement validity to', inputType: 'date' },
      { key: 're_agreement_date', label: 'Re-agreement date', inputType: 'date' },
      { key: 'salary', label: 'Salary', inputType: 'number' },
      { key: 'terms', label: 'Terms', inputType: 'textarea' },
      { key: 'working_hours', label: 'Working hours', inputType: 'text' },
      { key: 'working_days', label: 'Working days', inputType: 'text' },
      { key: 'replacement_working_days', label: 'Replacement working days', inputType: 'number' },
      { key: 'payment_before_date', label: 'Payment before date', inputType: 'number' },
      { key: 'for_agency', label: 'For agency', inputType: 'text' },
      { key: 'for_client', label: 'For client', inputType: 'text' }
    ]
  },
  {
    title: 'SERVICE PROVIDER',
    fields: [
      { key: 'service_provider', label: 'Service provider', inputType: 'text' },
      { key: 'service_provider_address', label: 'Service provider address', inputType: 'textarea' },
      { key: 'service_provider_represented_by', label: 'Service provider represented by', inputType: 'text' }
    ]
  }
];

// Convert dd-mm-yyyy (backend) to yyyy-mm-dd (HTML5 date input)
function toDateInputValue(val) {
  if (!val) return '';
  const s = String(val).trim();
  const ddMmYyyy = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (ddMmYyyy) return `${ddMmYyyy[3]}-${ddMmYyyy[2].padStart(2, '0')}-${ddMmYyyy[1].padStart(2, '0')}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return '';
}

// Convert yyyy-mm-dd (HTML5 date input) to dd-mm-yyyy (backend)
function fromDateInputValue(val) {
  if (!val) return '';
  const s = String(val).trim();
  const yyyyMmDd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (yyyyMmDd) return `${yyyyMmDd[3].padStart(2, '0')}-${yyyyMmDd[2]}-${yyyyMmDd[1]}`;
  return val;
}

// Apply default values for empty fields (used when creating/loading agreement)
function applyFieldDefaults(values) {
  const v = { ...(values || {}) };
  if (!(v.terms || '').trim()) v.terms = 'No Additional Terms';
  if (!(v.service_provider_address || '').trim()) v.service_provider_address = 'Office No. 210, Ravija Plaza, Thaltej Shilaj Road, Ahmedabad, Gujarat, India - 380059';
  if (!(v.service_provider_represented_by || '').trim()) v.service_provider_represented_by = 'Mr. PruthviRaj Kabira';
  if (!(v.for_agency || '').trim()) v.for_agency = (v.service_provider || '').trim() || 'Khodal Housekeeping Agency';
  if (!(v.for_client || '').trim()) v.for_client = (v.company_name || '').trim();
  return v;
}

const STATUS_LABELS = {
  draft: 'Draft',
  generated: 'Generated',
  shared_to_client: 'Shared to Client',
  accepted: 'Accepted',
  rejected: 'Rejected',
  hold: 'Hold'
};

function AdminAgreementsSection() {
  const [subTab, setSubTab] = useState('agreements');
  const [agreements, setAgreements] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [wizardForm, setWizardForm] = useState({
    agreement_type: 'company',
    agreement_template_id: '',
    company_id: '',
    employee_id: '',
    field_values: {}
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showMappingForm, setShowMappingForm] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, dir: 'asc' });
  const toast = useToast();

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3000';
  const baseUrl = apiBase.replace(/\/api\/v1\/?$/, '') || apiBase;

  const loadAgreements = useCallback(async () => {
    try {
      const { data } = await api.get('/agreements');
      setAgreements((data || []).map(a => ({ ...a, id: normId(a) })));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load agreements');
    }
  }, [toast]);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get('/agreement_templates');
      setTemplates((data || []).map(t => ({ ...t, id: normId(t) })));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load templates');
    }
  }, [toast]);

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await api.get('/companies');
      setCompanies((data || []).map(c => ({ ...c, id: normId(c) })));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadEmployees = useCallback(async () => {
    try {
      const { data } = await api.get('/employees');
      setEmployees((data || []).map(em => {
        const cid = em.company_id;
        const companyIdStr = cid == null ? '' : (typeof cid === 'object' && cid?.$oid ? cid.$oid : String(cid));
        return { ...em, id: normId(em), company_id: companyIdStr };
      }));
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadAgreements();
    loadTemplates();
    loadCompanies();
    loadEmployees();
  }, [loadAgreements, loadTemplates, loadCompanies, loadEmployees]);

  const filteredTemplates = (type) =>
    (templates || []).filter(t => !type || t.template_type === type);

  const handleCreateAgreement = () => {
    setShowCreateWizard(true);
    setCreateStep(1);
    setWizardForm({ agreement_type: 'company', agreement_template_id: '', company_id: '', employee_id: '', field_values: {} });
  };

  const handleEditAgreement = async (agreement) => {
    try {
      const { data } = await api.get(`/agreements/${normId(agreement)}`);
      const aid = normId(data);
      const tid = normId(data.agreement_template_id || data.agreement_template);
      let fieldValues = {};
      if (tid) {
        try {
          const res = await api.get(`/agreements/${aid}/preview_fields`);
          fieldValues = applyFieldDefaults(res.data || {});
          Object.entries(data.field_values || {}).forEach(([k, v]) => {
            if (v != null && v !== '') fieldValues[k] = v;
          });
        } catch (_) {
          fieldValues = data.field_values || {};
        }
      } else {
        fieldValues = applyFieldDefaults(data.field_values || {});
      }
      const base = {};
      EXPECTED_FIELD_KEYS.forEach(k => { if (!(k in fieldValues)) base[k] = ''; });
      fieldValues = { ...base, ...fieldValues };
      setWizardForm({
        agreement_type: data.agreement_type || 'company',
        agreement_template_id: tid || '',
        company_id: normId(data.company_id) || normId(data.company) || '',
        employee_id: data.employee_id ? normId(data.employee_id) : normId(data.employee) || '',
        field_values: fieldValues,
        _createdId: aid
      });
      setCreateStep(tid ? 5 : 2);
      setShowCreateWizard(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load agreement');
    }
  };

  const handleWizardNext = async () => {
    if (createStep === 2 && wizardForm._createdId && wizardForm.agreement_template_id) {
      setLoadingPreview(true);
      try {
        await api.put(`/agreements/${wizardForm._createdId}`, {
          agreement: { agreement_template_id: wizardForm.agreement_template_id }
        });
        const res = await api.get(`/agreements/${wizardForm._createdId}/preview_fields`);
        const fields = applyFieldDefaults(res.data);
        setWizardForm(f => ({ ...f, field_values: fields }));
        setCreateStep(5);
      } catch (err) {
        toast.error(err.response?.data?.errors?.join('\n') || err.response?.data?.error || 'Failed to assign template');
        return;
      } finally {
        setLoadingPreview(false);
      }
    } else if (createStep === 4) {
      setLoadingPreview(true);
      try {
        const payload = {
          agreement: {
            agreement_type: wizardForm.agreement_type,
            agreement_template_id: wizardForm.agreement_template_id,
            template_id: wizardForm.agreement_template_id,
            company_id: wizardForm.company_id,
            employee_id: wizardForm.employee_id || undefined,
            status: 'draft',
            field_values: wizardForm.field_values
          }
        };
        const { data } = await api.post('/agreements', payload);
        const aid = normId(data);
        const res = await api.get(`/agreements/${aid}/preview_fields`);
        const fields = applyFieldDefaults(res.data);
        setWizardForm(f => ({ ...f, _createdId: aid, field_values: fields }));
        setCreateStep(5);
      } catch (err) {
        toast.error(err.response?.data?.errors?.join('\n') || 'Failed to create agreement');
        return;
      } finally {
        setLoadingPreview(false);
      }
    } else if (createStep < 5) {
      setCreateStep(createStep + 1);
    }
  };

  const handleWizardBack = () => {
    if (createStep > 1) {
      if (createStep === 5 && wizardForm._createdId && !wizardForm.agreement_template_id) {
        setCreateStep(2);
      } else {
        setCreateStep(createStep - 1);
      }
    }
  };

  const handleSaveDraft = async () => {
    const aid = wizardForm._createdId;
    if (!aid) return;
    setSaving(true);
    try {
      const payload = {
        agreement: {
          field_values: wizardForm.field_values,
          agreement_type: wizardForm.agreement_type,
          company_id: wizardForm.company_id || undefined,
          employee_id: wizardForm.employee_id || undefined
        }
      };
      if (wizardForm.agreement_template_id) {
        payload.agreement.agreement_template_id = wizardForm.agreement_template_id;
      }
      await api.put(`/agreements/${aid}`, payload);
      toast.success('Saved as draft');
      setShowCreateWizard(false);
      loadAgreements();
    } catch (err) {
      toast.error(err.response?.data?.errors?.join('\n') || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAgreement = async () => {
    const aid = wizardForm._createdId;
    if (!aid) return;
    if (!wizardForm.agreement_template_id) {
      toast.error('Please select a template first. Use Back to go to step 2.');
      return;
    }
    setGeneratingPdf(true);
    try {
      const payload = {
        agreement: {
          field_values: wizardForm.field_values,
          agreement_template_id: wizardForm.agreement_template_id,
          agreement_type: wizardForm.agreement_type,
          company_id: wizardForm.company_id || undefined,
          employee_id: wizardForm.employee_id || undefined
        }
      };
      await api.put(`/agreements/${aid}`, payload);
      await api.post(`/agreements/${aid}/generate_pdf`);
      toast.success('Agreement generated');
      setShowCreateWizard(false);
      loadAgreements();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.errors?.join('\n') || 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadPdf = (agreement) => {
    const path = agreement.pdf_path;
    if (!path) {
      toast.error('No PDF available');
      return;
    }
    const url = `${baseUrl}${path}`;
    window.open(url, '_blank');
  };

  const handleDeleteAgreement = async (agreement) => {
    if (!window.confirm('Are you sure you want to delete this agreement? The associated PDF will also be deleted.')) return;
    try {
      await api.delete(`/agreements/${normId(agreement)}`);
      toast.success('Agreement deleted');
      loadAgreements();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete agreement');
    }
  };

  const handleAddTemplate = async () => {
    const name = window.prompt('Template name:');
    const type = window.prompt('Type (company | employee):', 'company');
    if (!name || !['company', 'employee'].includes(type)) return;
    try {
      await api.post('/agreement_templates', {
        agreement_template: { name, template_type: type }
      });
      toast.success('Template created. Upload PDF to complete.');
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.errors?.join('\n') || 'Failed to create template');
    }
  };

  const handleUploadTemplatePdf = async (template, file) => {
    if (!file || file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('pdf_file', file);
      await api.post(`/agreement_templates/${normId(template)}/upload_pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('PDF uploaded');
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload PDF');
    }
  };

  const [existingMappings, setExistingMappings] = useState([]);
  const [loadingMappingEditor, setLoadingMappingEditor] = useState(false);

  const openMappingEditor = async (template) => {
    setLoadingMappingEditor(true);
    try {
      const { data } = await api.get(`/agreement_template_mappings?template_id=${normId(template)}`);
      setExistingMappings(data || []);
      setEditingTemplate(template);
      setShowMappingForm(true);
    } catch {
      setExistingMappings([]);
      toast.error('Failed to load mappings');
    } finally {
      setLoadingMappingEditor(false);
    }
  };

  const handleSaveMappings = async (mappingsPayload) => {
    if (!editingTemplate) return;
    const valid = mappingsPayload.filter(m => 
      m.field_key && 
      typeof m.x_position === 'number' && !Number.isNaN(m.x_position) &&
      typeof m.y_position === 'number' && !Number.isNaN(m.y_position)
    );
    if (valid.length === 0) {
      toast.error('No valid mappings to save. Click on the PDF to place fields before saving.');
      return;
    }
    try {
      await api.post('/agreement_template_mappings/bulk_update', {
        template_id: normId(editingTemplate),
        mappings: valid
      });
      toast.success('Mappings saved');
      setShowMappingForm(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.errors?.join('\n') || 'Failed to save mappings');
    }
  };

  function filterAndSort(list, q, sortKey, sortDir, textFields) {
    let arr = [...(list || [])];
    if ((q || '').trim()) {
      const t = q.toLowerCase().trim();
      arr = arr.filter(item => textFields.some(f => (item[f] || '').toLowerCase().includes(t)));
    }
    if (sortKey && sortDir) {
      arr.sort((a, b) => {
        const va = a[sortKey] ?? '';
        const vb = b[sortKey] ?? '';
        const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return arr;
  }

  const filteredAgreements = filterAndSort(agreements, filterText, sortConfig.key, sortConfig.dir, ['id', 'agreement_type', 'company_name', 'employee_name', 'status']);

  return (
    <div className="admin-agreements-section">
      <div className="agreements-sub-tabs">
        <button className={subTab === 'agreements' ? 'active' : ''} onClick={() => { setSubTab('agreements'); setShowCreateWizard(false); setShowMappingForm(false); }}>Agreements</button>
        <button className={subTab === 'templates' ? 'active' : ''} onClick={() => { setSubTab('templates'); setShowCreateWizard(false); setShowMappingForm(false); }}>Templates</button>
      </div>

      {subTab === 'agreements' && (
        <>
          <div className="list-header">
            <h2>Agreements</h2>
            <button className="btn-add" onClick={handleCreateAgreement}>+ Create Agreement</button>
          </div>

          <div className="table-toolbar">
            <input
              type="text"
              placeholder="Filter agreements..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="table-filter-input"
            />
          </div>

          <div className="items-list">
            <table className="items-table agreements-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => setSortConfig(s => ({ key: 'id', dir: s.key === 'id' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Agreement ID {sortConfig.key === 'id' && (sortConfig.dir === 'asc' ? '▲' : '▼')}</th>
                  <th>Type</th>
                  <th>Company</th>
                  <th>Employee</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>PDF</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAgreements.length === 0 ? (
                  <tr><td colSpan={9} className="empty-row">No agreements. Click "+ Create Agreement" to add one.</td></tr>
                ) : filteredAgreements.map(a => (
                  <tr key={a.id}>
                    <td><code>{a.id?.slice(-8) || '—'}</code></td>
                    <td>{a.agreement_type === 'employee' ? 'Employee' : 'Company'}</td>
                    <td>{a.company_name || '—'}</td>
                    <td>{a.employee_name || '—'}</td>
                    <td><span className={`status-badge status-${a.status}`}>{STATUS_LABELS[a.status] || a.status}</span></td>
                    <td>{a.created_at ? new Date(a.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                    <td>{a.updated_at ? new Date(a.updated_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                    <td>
                      {a.pdf_path ? (
                        <button type="button" className="btn-pdf-download" onClick={() => handleDownloadPdf(a)} title="Download PDF" aria-label="Download PDF"><PDFIcon /></button>
                      ) : (
                        <span className="no-pdf">—</span>
                      )}
                    </td>
                    <td className="agreement-actions-cell">
                      <button type="button" className="btn-edit-agreement" onClick={() => handleEditAgreement(a)} title="Edit agreement"><EditIcon /><span className="btn-action-text">Edit</span></button>
                      <button type="button" className="btn-delete-agreement" onClick={() => handleDeleteAgreement(a)} title="Delete agreement"><DeleteIcon /><span className="btn-action-text">Delete</span></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {subTab === 'templates' && (
        <>
          <div className="list-header">
            <h2>Agreement Templates</h2>
            <button className="btn-add" onClick={handleAddTemplate}>+ Add Template</button>
          </div>
          <p className="templates-format-hint">Use <strong>A4</strong> (595 × 842 pt) PDFs for accurate field mapping. Re-upload to replace a template’s PDF.</p>
          <div className="templates-list">
            {templates.length === 0 ? (
              <p className="empty-message">No templates. Click "+ Add Template" to create one, then upload a PDF and configure field mappings.</p>
            ) : (
              templates.map(t => (
                <div key={t.id} className="template-card">
                  <div className="template-info">
                    <strong>{t.name}</strong>
                    <span className="template-type">{t.template_type}</span>
                    {t.pdf_file_path ? <span className="template-has-pdf">PDF uploaded</span> : <span className="template-no-pdf">No PDF</span>}
                  </div>
                  <div className="template-actions">
                    <label className={t.pdf_file_path ? 'btn-reupload-pdf' : 'btn-upload-pdf'}>
                      {t.pdf_file_path ? 'Re-upload PDF' : 'Upload PDF'}
                      <input type="file" accept=".pdf" onChange={(e) => { const f = e.target.files[0]; if (f) handleUploadTemplatePdf(t, f); e.target.value = ''; }} hidden />
                    </label>
                    <button type="button" className="btn-mapping" onClick={() => openMappingEditor(t)} disabled={loadingMappingEditor}>
                      {loadingMappingEditor ? 'Loading…' : 'Field Mappings'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {showCreateWizard && (
        <div className="modal-overlay" onClick={() => !loadingPreview && !generatingPdf && !saving && setShowCreateWizard(false)}>
          <div className={`modal-content agreements-wizard ${createStep === 5 ? 'agreements-wizard-fields-step' : ''}`} onClick={e => e.stopPropagation()}>
            <h3>{wizardForm._createdId ? 'Edit Agreement' : 'Create Agreement'}</h3>
            <div className="wizard-steps">
              <span className={createStep >= 1 ? 'active' : ''}>1. Type</span>
              <span className={createStep >= 2 ? 'active' : ''}>2. Template</span>
              <span className={createStep >= 3 ? 'active' : ''}>3. Company/Employee</span>
              <span className={createStep >= 4 ? 'active' : ''}>4. Review</span>
              <span className={createStep >= 5 ? 'active' : ''}>5. Fields</span>
            </div>

            {createStep === 1 && (
              <div className="wizard-step-content">
                <label>Agreement Type</label>
                <select value={wizardForm.agreement_type} onChange={e => setWizardForm(f => ({ ...f, agreement_type: e.target.value, agreement_template_id: '', employee_id: '' }))}>
                  <option value="company">Company Agreement</option>
                  <option value="employee">Employee Agreement</option>
                </select>
              </div>
            )}

            {createStep === 2 && (
              <div className="wizard-step-content">
                <label>Select Template</label>
                <select value={wizardForm.agreement_template_id} onChange={e => setWizardForm(f => ({ ...f, agreement_template_id: e.target.value }))} required>
                  <option value="">— Select —</option>
                  {filteredTemplates(wizardForm.agreement_type).filter(t => t.pdf_file_path).map(t => (
                    <option key={normId(t)} value={normId(t)}>{t.name}</option>
                  ))}
                </select>
                {filteredTemplates(wizardForm.agreement_type).filter(t => !t.pdf_file_path).length > 0 && (
                  <p className="wizard-hint">Some templates have no PDF yet. Upload a PDF in the Templates tab.</p>
                )}
              </div>
            )}

            {createStep === 3 && (
              <div className="wizard-step-content">
                <label>Company</label>
                <select value={wizardForm.company_id} onChange={e => setWizardForm(f => ({ ...f, company_id: e.target.value, employee_id: '' }))} required>
                  <option value="">— Select —</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.company_name}</option>
                  ))}
                </select>
                <label>Employee (for whom this agreement is created)</label>
                <select value={wizardForm.employee_id} onChange={e => setWizardForm(f => ({ ...f, employee_id: e.target.value }))} required>
                  <option value="">— Select —</option>
                  {employees.filter(em => em.company_id === wizardForm.company_id).map(em => (
                    <option key={em.id} value={em.id}>{em.full_name}</option>
                  ))}
                </select>
                {wizardForm.company_id && employees.filter(em => em.company_id === wizardForm.company_id).length === 0 && (
                  <p className="wizard-hint">No employees found for this company. Add employees to the company first.</p>
                )}
              </div>
            )}

            {createStep === 4 && (
              <div className="wizard-step-content">
                <p>Review your selection:</p>
                <ul>
                  <li>Type: {wizardForm.agreement_type}</li>
                  <li>Company: {companies.find(c => c.id === wizardForm.company_id)?.company_name}</li>
                  <li>Employee: {employees.find(em => em.id === wizardForm.employee_id)?.full_name || '—'}</li>
                </ul>
                <p>Click Next to create draft and auto-fill fields.</p>
              </div>
            )}

            {createStep === 5 && (
              <div className="wizard-step-content wizard-fields wizard-fields-full">
                {loadingPreview && <p className="loading-indicator">Loading auto-filled fields...</p>}
                {!loadingPreview && (
                  <>
                    {wizardForm._createdId && !wizardForm.agreement_template_id && (
                      <div className="wizard-alert">
                        <strong>No template assigned.</strong> Select a template below or use Back to go to step 2.
                        <label>Select Template</label>
                        <select value={wizardForm.agreement_template_id} onChange={e => setWizardForm(f => ({ ...f, agreement_template_id: e.target.value }))} required>
                          <option value="">— Select —</option>
                          {filteredTemplates(wizardForm.agreement_type).filter(t => t.pdf_file_path).map(t => (
                            <option key={normId(t)} value={normId(t)}>{t.name}</option>
                          ))}
                        </select>
                        <hr />
                      </div>
                    )}
                    <p className="wizard-fields-hint">Edit any fields below, then save as Draft or Generate Agreement (PDF).</p>
                    <div className="wizard-fields-sections">
                      {FIELD_SECTIONS.map(section => {
                        const allKeys = Object.keys(wizardForm.field_values || {});
                        const knownKeys = new Set(FIELD_SECTIONS.flatMap(s => s.fields.map(f => f.key)));
                        const otherFields = allKeys.filter(k => !knownKeys.has(k));
                        const hasFields = section.fields.some(f => allKeys.includes(f.key));
                        if (!hasFields && section.title !== 'OTHER') return null;
                        if (section.title === 'OTHER' && otherFields.length === 0) return null;
                        return (
                          <div key={section.title} className="wizard-field-section">
                            <h4 className="wizard-field-section-title">{section.title}</h4>
                            <div className="wizard-field-section-grid">
                              {section.fields.filter(f => allKeys.includes(f.key)).map(({ key, label, inputType }) => {
                                const val = wizardForm.field_values?.[key] ?? '';
                                const isDate = inputType === 'date';
                                const displayVal = isDate ? toDateInputValue(val) : (val || '');
                                const handleChange = e => {
                                  const v = e.target.value;
                                  setWizardForm(f => ({
                                    ...f,
                                    field_values: { ...(f.field_values || {}), [key]: isDate ? fromDateInputValue(v) : v }
                                  }));
                                };
                                if (inputType === 'textarea') {
                                  return (
                                    <div key={key} className="field-value-row field-value-row-span">
                                      <label>{label}</label>
                                      <textarea value={val || ''} onChange={handleChange} rows={2} />
                                    </div>
                                  );
                                }
                                return (
                                  <div key={key} className="field-value-row">
                                    <label>{label}</label>
                                    <input
                                      type={inputType === 'number' ? 'number' : inputType === 'date' ? 'date' : inputType === 'email' ? 'email' : inputType === 'tel' ? 'tel' : 'text'}
                                      value={displayVal}
                                      onChange={handleChange}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                      {(() => {
                        const allKeys = Object.keys(wizardForm.field_values || {});
                        const knownKeys = new Set(FIELD_SECTIONS.flatMap(s => s.fields.map(f => f.key)));
                        const otherFields = allKeys.filter(k => !knownKeys.has(k));
                        if (otherFields.length === 0) return null;
                        return (
                          <div className="wizard-field-section">
                            <h4 className="wizard-field-section-title">OTHER</h4>
                            <div className="wizard-field-section-grid">
                              {otherFields.map(key => {
                                const val = wizardForm.field_values?.[key] ?? '';
                                return (
                                  <div key={key} className="field-value-row">
                                    <label>{key.replace(/_/g, ' ')}</label>
                                    <input type="text" value={val || ''} onChange={e => setWizardForm(f => ({ ...f, field_values: { ...(f.field_values || {}), [key]: e.target.value } }))} />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    {Object.keys(wizardForm.field_values || {}).length === 0 && <p className="no-fields">No fields available. Configure mappings on the template.</p>}
                  </>
                )}
              </div>
            )}

            <div className="wizard-actions">
              {(createStep > 1 && createStep < 5) || (createStep === 5 && wizardForm._createdId) ? <button type="button" onClick={handleWizardBack}>Back</button> : null}
              {createStep < 5 && <button type="button" onClick={handleWizardNext} disabled={loadingPreview || (createStep === 2 && !wizardForm.agreement_template_id) || (createStep === 3 && (!wizardForm.company_id || !wizardForm.employee_id))}>Next</button>}
              {createStep === 5 && (
                <>
                  <button type="button" onClick={handleSaveDraft} disabled={saving || generatingPdf}>Save as Draft</button>
                  <button type="button" className="btn-primary" onClick={handleGenerateAgreement} disabled={saving || generatingPdf}>
                    {generatingPdf ? 'Generating...' : 'Generate Agreement'}
                  </button>
                </>
              )}
              <button type="button" className="btn-cancel" onClick={() => setShowCreateWizard(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showMappingForm && editingTemplate && editingTemplate.pdf_file_path && (
        <div className="modal-overlay" onClick={() => setShowMappingForm(false)}>
          <div className="modal-content visual-mapping-modal" onClick={e => e.stopPropagation()}>
            <VisualMappingEditor
              key={normId(editingTemplate)}
              template={editingTemplate}
              pdfUrl={editingTemplate.pdf_file_path.startsWith('http') ? editingTemplate.pdf_file_path : `${baseUrl}${editingTemplate.pdf_file_path}`}
              existingMappings={existingMappings}
              onSave={handleSaveMappings}
              onCancel={() => setShowMappingForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAgreementsSection;
